package com.starparty.app.astro0.readers

import com.starparty.app.astro0.SP_Time
import com.starparty.app.astro0.UMS
import com.starparty.app.astro0.celestialobjects.DSO

/**
 * Title:        Starparty Astro-0
 * Description:  Simply astronomy package for starparty.com and sennen-cove-cornwall.uk
 * Copyright:    Copyright (c) 2021
 * Company: starparty.com
 * @author Rob Leeson
 * @version 2.0
 */
object DSOReader {
    /** Column numbers in star data file.  */
    internal const val RA = 4
    internal const val DEC = 5
    internal const val MAG = 3
    internal const val TYPE = 2
    internal const val MES = 0
    internal const val NAME = 1

    internal const val SEPARATOR = '|'

    fun makeObjects(time: SP_Time /*, InputStream is*/): Array<DSO> {
        val dsoTemp: ArrayList<DSO> = createObjects(time /*, is*/)
        return dsoTemp.toTypedArray()
    }

    internal fun createObjects(time: SP_Time /*, InputStream is*/): ArrayList<DSO> {
        //    val filename: String = SkyRenderer.Companion.starDataFileName
        val dsos: ArrayList<DSO> = ArrayList()
        var line: String? = null

        for (l in mlines.indices) {
            line = mlines[l]
            dsos.add(createObject(line, time))
        }
        return dsos
    }

    internal fun createObject(data: String, time: SP_Time): DSO {
        val columns = breakIntoColumns(data)
        val ra: UMS = UMS.parseUMSStringToUMS(columns[RA])
        val dec: UMS = UMS.parseUMSStringToUMS(columns[DEC])
        val mag = columns[MAG].toDouble()
        val type = columns[TYPE]
        val mesNum = columns[MES].toInt()
        val name = columns[NAME]

        return DSO(time, ra, dec, mag, type, mesNum, name)
    }

    internal fun breakIntoColumns(data: String): Array<String> {
        var start = 0
        var col = 0
        val columns = data.split(SEPARATOR).toTypedArray()

        return columns
    }

    private val mlines =
        arrayOf(
    "45|Pleiades|OC|1.6|03:47:24|24:07:00",
    "7|Ptolemy Cluster|OC|3.3|17:53:51.2|-34:47:34",
    "31|Andromeda Galaxy|SG|3.4|0:42:44.3|41:16:9",
    "44|Beehive Cluster or Praesepe|OC|3.7|8:40.4:0|19:59:00",
    "42|Orion Nebula|HII|4.0|5:35:17.3|-5:23:28",
    "6|Butterfly Cluster|OC|4.2|17:40.1:00|-32:13:00",
    "47||OC|4.2|7:36.6:0|-14:30:0",
    "41||OC|4.5|6:46:0|-20:46:0",
    "25||OC|4.6|18:31.6:0|-19:15:0",
    "52||OC|5.0|23:24.2:0|61:35:0",
    "22|Sagittarius Cluster|GC|5.1|18:36:23.94|-23:54:17.1",
    "35||OC|5.3|6:9.1:0|24:21:00",
    "34||OC|5.5|2:42.1:0|42:46:0",
    "39||OC|5.5|21:31:42|48:26:00",
    "48||OC|5.5|8:13.7:0|-5:45:0",
    "33|Triangulum Galaxy|SG|5.7|1:33:50.02|30:39:36.7",
    "13|Hercules Great Globular Cluster|GC|5.8|16:41:41.24|36:27:35.5",
    "4||GC|5.9|16:23:35.22|-26:31:32.7",
    "50||OC|5.9|7:3.2:0|-8:20:0",
    "8|Lagoon Nebula|NwC|6.0|18:3:37|-24:23:12",
    "16|Eagle Nebula|HIIwC|6.0|18:18:48|-13:49:0",
    "17|Omega, Swan, Horseshoe, or Lobster Nebula|HIIwC|6.0|18:20:26|-16:10:36",
    "93||OC|6.0|7:44.6:0|-23:52:0",
    "46||OC|6.1|7:41.8:0|-14:49:0",
    "67||OC|6.1|8:51.3:0|11:49:0",
    "71||GC|6.1|19:53:46.49|18:46:45.1",
    "3||GC|6.2|13:42:11.62|28:22:38.2",
    "15||GC|6.2|21:29:58.33|12:10:1.2",
    "37||OC|6.2|5:52:18|32:33:2",
    "2||GC|6.3|21:33:27.02|-0:49:23.7",
    "11|Wild Duck Cluster|OC|6.3|18:51.1:0|-6:16:0",
    "20|Trifid Nebula|HIIwC|6.3|18:02:23|-23:01:48",
    "36||OC|6.3|5:36:12|34:8:4",
    "92||GC|6.3|17:17:7.39|43:8:9.4",
    "10||GC|6.4|16:57:8.92|-4:5:58.07",
    "21||OC|6.5|18:4.6:0|-22:30:0",
    "5||GC|6.7|15:18:33.22|2:4:51.7",
    "23||OC|6.9|17:56.8:0|-19:1:0",
    "81|Bode's Galaxy|SG|6.9|9:55:33.2|69:3:55",
    "29|Cooling Tower|OC|7.1|20:23:56|38:31:24",
    "38||OC|7.4|5:28:42|35:51:18",
    "55||GC|7.4|19:39:59.71|-30:57:53.1",
    "62||GC|7.4|17:1:12.60|-30:6:44.5",
    "103||OC|7.4|1:33.2:0|60:42:0",
    "18||OC|7.5|18:19.9:0|-17:08:0",
    "19||GC|7.5|17:2:37.69|-26:16:4.6",
    "27|Dumbbell Nebula|PN|7.5|19:59:36.340|22:43:16.09",
    "83|Southern Pinwheel Galaxy|BSG|7.5|13:37:0.9|-29:51:57",
    "12||GC|7.7|16:47:14.18|-1:56:54.7",
    "28||GC|7.7|18:24:32.89|-24:52:11.4",
    "30||GC|7.7|21:40:22.12|-23:10:47.5",
    "80||GC|7.9|16:17:2.41|-22:58:33.9",
    "101|Pinwheel Galaxy|SG|7.9|14:3:12.6|54:20:57",
    "26||OC|8.0|18:45.2:0|-9:24:0",
    "32||DEG|8.1|0:42:41.8|40:51:55",
    "14||GC|8.3|17:37:36.15|-3:14:45.3",
    "53||GC|8.3|13:12:55.25|18:10:5.4",
    "56||GC|8.3|19:16:35.57|30:11:0.5",
    "69||GC|8.3|18:31:23.10|-32:20:53.1",
    "78||DN|8.3|5:46:46.7|0:0:50",
    "1|Crab Nebula|SR|8.4|5:34:31.94|22:00:52.2",
    "9||GC|8.4|17:19:11.78|-18:30:58.5",
    "51|Whirlpool Galaxy|SG|8.4|13:29:52.7|47:11:43",
    "54||GC|8.4|18:55:3.33|-30:28:47.5",
    "82|Cigar Galaxy|SBG|8.4|9:55:52.2|69:40:47",
    "79||GC|8.6|5:24:10.59|-24:31:27.3",
    "57|Ring Nebula|PN|8.8|18:53:35.079|33:01:45.03",
    "66|Leo Triplet|BSG|8.9|11:20:15.0|12:59:30",
    "107||GC|8.9|16:32:31.86|-13:3:13.6",
    "43|De Mairan's Nebula|HII|9.0|5:35:36|-5:16:0",
    "73||A|9.0|20:58:54|-12:38:0",
    "94|Croc's Eye or Cat's Eye Galaxy|SG|9.0|12:50:53.1|41:07:14",
    "104|Sombrero Galaxy|SG|9.0|12:39:59.4|-11:37:23",
    "110||DEG|9.0|0:40:22.1|41:41:07",
    "70||GC|9.1|18:43:12.76|-32:17:31.6",
    "106||SG|9.1|12:18:57.5|47:18:14",
    "75||GC|9.2|20:06:4.75|-21:55:16.2",
    "63|Sunflower Galaxy|SG|9.3|13:15:49.3|42:01:45",
    "49||EG|9.4|12:29:46.7|8:0:2",
    "64|Black Eye Galaxy|SG|9.4|12:56:43.7|21:40:58",
    "72||GC|9.4|20:53:27.7|-12:32:14.3",
    "77|Cetus A|SG|9.6|2:42:40.7|0:0:48",
    "87|Virgo A|EG|9.6|12:30:49.42338|12:23:28.0439",
    "40|Winnecke-4|SS|9.7|12:22:12.5|58:4:59",
    "68||GC|9.7|12:39:27.98|-26:44:38.6",
    "60||EG|9.8|12:43:39.6|11:33:9",
    "86||LG|9.8|12:26:11.7|12:56:46",
    "97|Owl Nebula|PN|9.9|11:14:47.734|55:1:8.5",
    "74|Phantom Galaxy|SG|10.0|1:36:41.8|15:47:1",
    "85||LG|10.0|12:25:24.0|18:11:28",
    "76|Little Dumbbell Nebula|PN|10.1|1:42:24|51:34:31"

            )
}